import React from "react";
import { Link } from "react-router-dom";
import { Database, Search, PenTool, Radio, Activity, ArrowUpRight } from "lucide-react";

export const SERVICES = [
  {
    n: "01",
    slug: "data-products-engineering",
    icon: Database,
    title: "Data Products & Engineering",
    blurb: "Build and maintain databases, pipelines, and digital tools.",
  },
  {
    n: "02",
    slug: "research-analysis",
    icon: Search,
    title: "Research & Analysis",
    blurb: "Find, combine, and interpret the figures that matter.",
  },
  {
    n: "03",
    slug: "communication-storytelling",
    icon: PenTool,
    title: "Data Storytelling",
    blurb: "Turn evidence into clear and powerful content.",
  },
  {
    n: "04",
    slug: "distribution-reach",
    icon: Radio,
    title: "Publishing & Reach",
    blurb: "Package content to reach audiences through platforms and algorithms.",
  },
  {
    n: "05",
    slug: "fresh-data-updates",
    icon: Activity,
    title: "Fresh Data & Updates",
    blurb:
      "Keep information current through live feeds and regular updates.",
  },
];

export default function WhatWeDo() {
  return (
    <section
      id="approach"
      data-testid="what-we-do-section"
      className="py-24 md:py-32 bg-[#0A0A0A] text-white relative overflow-hidden"
    >
      <div className="absolute inset-0 dot-bg opacity-[0.08] pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        {/* Section header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 md:mb-20">
          <div className="lg:col-span-6">
            <p className="overline mb-4 text-[#FDE047]">Services</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1] text-white">
              Build your case,
              <br />
              <span className="text-[#0052FF]">end to end.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-lg text-white/70 leading-relaxed">
              Our data engineers, analysts and storytellers work across the data communications chain, from research and analysis, to delivering infrastructure and content.
            </p>
          </div>
        </div>

        {/* Editorial row list */}
        <ol
          data-testid="services-list"
          className="border-t border-white/15 list-none p-0 m-0"
        >
          {SERVICES.map((s) => (
            <ServiceRow key={s.slug} service={s} />
          ))}
        </ol>
      </div>
    </section>
  );
}

function ServiceRow({ service }) {
  const Icon = service.icon;
  return (
    <li className="list-none">
      <Link
        to={`/services/${service.slug}`}
        data-testid={`service-${service.slug}`}
        className="group relative flex flex-col md:grid md:grid-cols-12 md:gap-6 lg:gap-10 md:items-center py-8 md:py-10 lg:py-14 border-b border-white/15 transition-colors duration-300 hover:bg-[#0052FF] -mx-6 md:-mx-10 lg:-mx-14 px-6 md:px-10 lg:px-14"
      >
        {/* Number */}
        <span
          aria-hidden="true"
          className="md:col-span-2 lg:col-span-2 font-display font-black tracking-tighter leading-none text-[3.5rem] md:text-6xl lg:text-[6rem] text-white/20 group-hover:text-[#FDE047] transition-colors mb-4 md:mb-0"
        >
          {service.n}
        </span>

        {/* Title + eyebrow */}
        <div className="md:col-span-6 lg:col-span-6 min-w-0">
          <div className="flex items-center gap-2.5 mb-3 md:mb-4">
            <Icon
              size={16}
              strokeWidth={1.5}
              className="text-[#0052FF] group-hover:text-[#FDE047] transition-colors"
            />
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.25em] text-white/40 group-hover:text-white/80 transition-colors">
              Service / {service.n}
            </span>
          </div>
          <h3 className="font-display font-black text-white tracking-tighter leading-[0.95] text-3xl md:text-[2.5rem] lg:text-[3.5rem] xl:text-[4rem]">
            {service.title}
          </h3>
        </div>

        {/* Blurb */}
        <p className="md:col-span-3 lg:col-span-3 text-sm md:text-[0.95rem] text-white/55 group-hover:text-white/90 leading-relaxed mt-5 md:mt-0 transition-colors">
          {service.blurb}
        </p>

        {/* Arrow */}
        <span className="md:col-span-1 lg:col-span-1 md:justify-self-end flex items-center gap-2 mt-6 md:mt-0">
          <span className="md:hidden font-mono text-[0.65rem] uppercase tracking-[0.25em] text-white/50 group-hover:text-white">
            Learn more
          </span>
          <ArrowUpRight
            size={32}
            strokeWidth={1.5}
            className="text-white/40 group-hover:text-[#FDE047] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all shrink-0"
          />
        </span>
      </Link>
    </li>
  );
}
